import { useState } from 'react';
import { Button, Dropdown, Flex, Layout, Menu, Spin, Tag } from 'antd';
import type { MenuProps } from 'antd';
import { Link, Outlet, useLocation, useNavigate, useNavigation } from 'react-router';
import { SupportCodeButton } from './support-code';
import { BrandLockup } from '../../components/brand';
import { OfflineBanner } from './offline-banner';
import { useCurrentUser, useSignOut } from '../../auth/use-session';
import { activeNavItem, navigationFor, NAV_GROUPS, ROLE_LABELS, type NavItem } from '../../auth/policy';
import { useClientConfig } from '../../features/church/use-client-config';
import { IdleSignOut } from './idle-sign-out';
import { NAV_GROUP_ICONS, NAV_ICONS } from '../../components/nav-icons';

const { Header, Sider, Content } = Layout;

/**
 * The visible entries, as a menu — folding each `group` into one collapsible
 * section (`NAV_GROUPS`).
 *
 * The section is emitted **where its first surviving member sits**, so the one
 * ordered list in `policy.ts` still decides the order and this function adds no
 * opinion of its own about it. That is why entries sharing a group have to be
 * contiguous there, which is the rule stated on `NavItem.group`.
 *
 * **A section holding one entry is rendered as that entry.** A PASTOR may reach
 * exactly one of the six, and folding a single destination behind a disclosure
 * they must open every time is a worse sidebar than the flat one they had —
 * the fold is there to shorten a long list, and their list is two items. So the
 * shape of the menu follows what a role can actually see rather than being
 * fixed for everyone.
 */
type MenuItem = NonNullable<MenuProps['items']>[number];

function toMenuItems(items: NavItem[]): MenuItem[] {
  const leaf = (item: NavItem): MenuItem => ({
    key: item.path,
    icon: NAV_ICONS[item.path],
    label: <Link to={item.path}>{item.label}</Link>,
  });

  const rendered = new Set<string>();
  return items.flatMap((item): MenuItem[] => {
    if (!item.group) return [leaf(item)];
    if (rendered.has(item.group)) return [];
    rendered.add(item.group);

    const children = items.filter((candidate) => candidate.group === item.group);
    if (children.length === 1) return [leaf(children[0])];

    return [
      {
        key: item.group,
        icon: NAV_GROUP_ICONS[item.group],
        // Plain text, not a `Link`: the row opens the section. A section that
        // also navigated would take somebody somewhere on the way to showing
        // them the choice they were opening it to make.
        label: NAV_GROUPS[item.group],
        children: children.map(leaf),
      },
    ];
  });
}

/**
 * The frame every authenticated screen renders inside
 * (docs/07-implementation-roadmap.md F1.8).
 *
 * **The sidebar is derived, not written.** `navigationFor(user)` filters the one
 * navigation list in `policy.ts` by the abilities that role actually has, so a
 * PASTOR sees Members and nothing else, a REGION_COORDINATOR sees no Users and
 * no Audit Log, and none of that is decided here. There is no role check in this
 * file — the closest thing to one is the label next to the user's email.
 */
export function AppShell() {
  const navigation = useNavigation();
  const location = useLocation();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const signOut = useSignOut();

  // Warmed here so it is in cache before any screen needs it, and requested
  // exactly once per session (F1.9). This is the only settings call the shell is
  // allowed to make — `GET /church` would 403 for two of the four roles.
  useClientConfig();

  const isNavigating = navigation.state !== 'idle';
  const items = navigationFor(user);

  // `/jemaat/abc` should light up `/jemaat`, and `/laporan/ulang-tahun-pernikahan`
  // must not light up `/laporan/ulang-tahun` — see `activeNavItem`. The root is
  // matched exactly, or it would be selected on every page.
  const active = activeNavItem(items, location.pathname);
  const selectedKey = active?.path ?? (location.pathname === '/' ? '/' : undefined);

  /**
   * Which sections are open — `null` until somebody says otherwise, and that
   * null is the whole design.
   *
   * While nobody has touched the disclosure, the sidebar **follows the route**:
   * the section holding the current screen is open, so the selected entry is
   * never hidden and the menu can say where you are. This matters on more than
   * a deep link — `Beranda` lists every section as a tile, so arriving at
   * `/keluarga` without going through the sidebar is an ordinary path, and a
   * `defaultOpenKeys` computed at mount would be right once and stale for the
   * rest of a session that never remounts.
   *
   * The first toggle hands it over: from then on the array is what the person
   * chose, and it survives navigation. Closing the section while standing
   * inside it therefore *works*, rather than springing back open — which is
   * what a derivation that always forced the active group would do, and a
   * control that visibly refuses to close is worse than one that occasionally
   * hides the highlight. The parent row still reads as selected.
   */
  const activeGroup = active?.group;
  const [chosenOpenKeys, setChosenOpenKeys] = useState<string[] | null>(null);
  const openKeys = chosenOpenKeys ?? (activeGroup ? [activeGroup] : []);

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {/* Above the header: the condition applies to the whole app, including
          the navigation, and a banner inside the content area would scroll
          away from the person it is explaining things to (F7.5). */}
      <OfflineBanner />

      <Header style={{ display: 'flex', alignItems: 'center', gap: 16, borderBottom: '1px solid #e2e9f5' }}>
        {/* The lockup is the way home, which is the convention every other app
            these staff use already taught them. Its accessible name comes from
            the words inside it — the mark's `alt` is empty so it is not read
            twice (`src/components/brand.tsx`). */}
        <Link to="/" style={{ color: 'inherit' }}>
          <BrandLockup />
        </Link>

        <Flex align="center" gap={12} style={{ marginLeft: 'auto' }}>
          {/* F6.7 — a support code available without waiting for a crash. */}
          <SupportCodeButton />
          {user ? <Tag>{ROLE_LABELS[user.role]}</Tag> : null}
          <Dropdown
            menu={{
              items: [
                {
                  key: 'sign-out',
                  label: 'Keluar',
                  onClick: () => {
                    void signOut().then(() => navigate('/masuk', { replace: true, state: { notice: 'signedOut' } }));
                  },
                },
              ],
            }}
          >
            <Button type="text">{user?.email ?? 'Akun'}</Button>
          </Dropdown>
        </Flex>
      </Header>

      <Layout>
        {/* 240 rather than the 220 this was flat: a folded entry pays for its
            indent out of the label, and at 220 "Permintaan Perubahan" — the
            longest of the six — was clipped to "Permintaan Perub…". A nav entry
            you cannot read is worse than a slightly narrower content column,
            and `inlineIndent` below buys back most of the difference. */}
        <Sider width={240} breakpoint="lg" collapsedWidth={0} style={{ borderRight: '1px solid #e2e9f5' }}>
          <Menu
            mode="inline"
            // 16 rather than AntD's 24. The indent only has to say "inside the
            // section", which it does at 16 beside a top-level row; the other
            // 8px is label.
            inlineIndent={16}
            selectedKeys={selectedKey ? [selectedKey] : []}
            openKeys={openKeys}
            onOpenChange={setChosenOpenKeys}
            style={{ borderInlineEnd: 'none' }}
            items={toMenuItems(items)}
          />
        </Sider>

        <Content style={{ padding: 24 }}>
          <Spin spinning={isNavigating} delay={150}>
            <Outlet />
          </Spin>
        </Content>
      </Layout>

      <IdleSignOut />
    </Layout>
  );
}
